import { Box, styled } from '@mui/material';
import EmptyGridStateIcon from 'assets/img/empty-grid-state.svg';
import {
  DataItem,
  DatasetDate,
  DatasetTitle,
} from 'FeatureModules/DataImport/Components/DataImport.styles';

import React, { FC } from 'react';
import Moment from 'react-moment';
import { Booleanish } from 'types/booleanish.types';
import {
  EGeoDataIssueSeverityLevel,
  IDataset,
  IGeoDataIssue,
} from 'FeatureModules/DataImport/types/dataset.types';
import FlexBox from 'Components/FlexBox';

interface IDatasetOverviewCardProps {
  selected: boolean;
  dataset: IDataset;
  issues: IGeoDataIssue[];
  onClick: () => void;
}

const ImageContainer = styled(FlexBox)(() => ({
  background: '#fff',
  borderRadius: 8,
  padding: '16px',
  marginBottom: 8,
}));

const IssueBadge = styled('div')<{ severity: 'error' | 'warning' | undefined }>(
  ({ theme, severity }) => ({
    background:
      severity === 'error'
        ? theme.custom.themeColors.error[60]
        : theme.custom.themeColors.accent[60],
    padding: '4px 8px',
    borderRadius: 16,
    fontWeight: 700,
    fontSize: '10px',
    whiteSpace: 'nowrap',
  })
);

const DatasetOverviewCard: FC<IDatasetOverviewCardProps> = ({
  selected,
  dataset,
  issues,
  onClick,
}) => {
  const errors = issues.filter(
    ({ severityLevel }) => severityLevel === EGeoDataIssueSeverityLevel.ERROR
  );

  const warnings = issues.filter(
    ({ severityLevel }) => severityLevel === EGeoDataIssueSeverityLevel.WARNING
  );

  return (
    <DataItem
      selected={Booleanish(selected)}
      severity={errors.length ? 'error' : warnings.length ? 'warning' : undefined}
      onClick={onClick}
      data-testid="dataset-overview-card"
    >
      <ImageContainer justifyContent="center" mb={2}>
        <img width={100} src={EmptyGridStateIcon} alt="empty" />
      </ImageContainer>
      {dataset.title ? (
        <DatasetTitle variant="BODY_MEDIUM_BOLD" autoOverflow>
          {dataset.title}
        </DatasetTitle>
      ) : (
        <DatasetTitle variant="BODY_MEDIUM_BOLD" unnamed autoOverflow>
          Unnamed
        </DatasetTitle>
      )}
      <Box display="flex" gap={1} alignItems="center">
        <DatasetDate>
          <Moment format="L">{dataset.createdTimestamp}</Moment>
        </DatasetDate>
        {!!errors.length && (
          <IssueBadge severity="error">
            {errors.length}
            {/* Prevent overflow issue and only show the text if the other badge is not present */}
            {!warnings.length && <> error{errors.length && errors.length > 1 && 's'}</>}
          </IssueBadge>
        )}
        {!!warnings.length && (
          <IssueBadge severity="warning">
            {warnings.length}
            {/* Prevent overflow issue and only show the text if the other badge is not present */}
            {!errors.length && <> warning{warnings.length && warnings.length > 1 && 's'}</>}
          </IssueBadge>
        )}
      </Box>
    </DataItem>
  );
};

export default DatasetOverviewCard;
